import { Navigate, useLocation } from "react-router-dom";

// Helper function to find first accessible route for a user
// Matches the order of navigation items and only returns routes user has permission for
const getFirstAccessibleRoute = (userRole, permissions) => {
  console.log('🔍 getFirstAccessibleRoute called:', { userRole, permissions });
  
  // Admin always has access to analytics
  if (userRole === 'Admin') {
    console.log('✅ Admin user - returning /dashboard/analytics');
    return '/dashboard/analytics';
  }
  
  // Company users default to products-management
  if (userRole === 'Company') {
    console.log('✅ Company user - returning /dashboard/products-management');
    return '/dashboard/products-management';
  }
  
  // For other roles, check permissions in navigation order
  // Only return routes the user actually has permission for
  // Order matches navigation.js: Dashboard -> App Order -> Agronomist ->  -> etc.
  // NOTE: We skip analytics/dashboard for non-Admin/Company users when redirecting from unauthorized routes
  // because analytics might not be the most appropriate landing page for their role
  
  // Skip analytics for other roles - go to first practical route
  // 1. App Order (skip analytics)
  if (permissions.appOrders) {
    console.log('✅ Found appOrders permission - returning /dashboard/orders-management');
    return '/dashboard/orders-management';
  }
  // 3. Agronomist
  if (permissions.talkToAgronomist) {
    console.log('✅ Found talkToAgronomist permission - returning /dashboard/agronomist');
    return '/dashboard/agronomist';
  }
  // 4. Bulk Order
  if (permissions.bulkOrders) {
    console.log('✅ Found bulkOrders permission - returning /dashboard/bulk-upload');
    return '/dashboard/bulk-upload';
  }
  // 5. Quotation/Proforma Invoice
  if (permissions.quotation) {
    console.log('✅ Found quotation permission - returning /dashboard/Quotation-proforma');
    return '/dashboard/Quotation-proforma';
  }
  // 6. Farmers
  if (permissions.farmerManagement) {
    console.log('✅ Found farmerManagement permission - returning /dashboard/farmers');
    return '/dashboard/farmers';
  }
  // 7. Agri-store (products-management)
  if (permissions.agriStore) {
    console.log('✅ Found agriStore permission - returning /dashboard/products-management');
    return '/dashboard/products-management';
  }
  // 8. Posts
  if (permissions.farmerForum) {
    console.log('✅ Found farmerForum permission - returning /dashboard/posts');
    return '/dashboard/posts';
  }
  // 9. Knowledge Hub (courses)
  if (permissions.successStories) {
    console.log('✅ Found successStories permission - returning /dashboard/courses');
    return '/dashboard/courses';
  }
  // 10. Event Types
  if (permissions.eventType) {
    console.log('✅ Found eventType permission - returning /dashboard/event-types');
    return '/dashboard/event-types';
  }
  // 11. Crop Types
  if (permissions.cropType) {
    console.log('✅ Found cropType permission - returning /dashboard/crop-types');
    return '/dashboard/crop-types';
  }
  // 12. Enquiry Form
  if (permissions.enquiry) {
    console.log('✅ Found enquiry permission - returning /dashboard/enquiry-form');
    return '/dashboard/enquiry-form';
  }
  // 13. Field Visit
  if (permissions.fieldVisit) {
    console.log('✅ Found fieldVisit permission - returning /dashboard/field-visit');
    return '/dashboard/field-visit';
  }
  // 14. Disease Advisory
  if (permissions.diseaseAdvisory) {
    console.log('✅ Found diseaseAdvisory permission - returning /dashboard/disease-advisory');
    return '/dashboard/disease-advisory';
  }
  // 15. Crop Hub
  if (permissions.cropHub) {
    console.log('✅ Found cropHub permission - returning /dashboard/crop-hub');
    return '/dashboard/crop-hub';
  }
  
  // Fallback: If user has analytics/dashboard permission but no other practical routes,
  // redirect to analytics (but only as last resort when redirecting from unauthorized routes)
  if (permissions.analytics || permissions.dashboard) {
    console.log('⚠️ No other permissions found, but has analytics - returning /dashboard/analytics as fallback');
    return '/dashboard/analytics';
  }
  
  // If user has no permissions at all, redirect to products-management as safe default
  console.log('⚠️ No permissions found at all - returning /dashboard/products-management as default');
  return '/dashboard/products-management';
};

const ProtectedRoute = ({ children, allowedRoles = [], requiredPermission = null }) => {
  const location = useLocation();
  const loggedData = JSON.parse(localStorage.getItem("logged_data")) || {};
  const userData = loggedData?.data;
  const isAuthenticated = !!userData;
  const userRole = userData?.user_type;
  const permissions = userData?.permissions || {};
  
  console.log('🚀 ProtectedRoute - Entry:', {
    pathname: location.pathname,
    userRole,
    isAuthenticated,
    allowedRoles,
    requiredPermission,
    permissions
  });
  
  // If user is not authenticated, redirect to login
  if (!isAuthenticated) {
    console.log('❌ User not authenticated - redirecting to /login');
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  // Block Farmer role from accessing dashboard - clear localStorage and redirect
  if (userRole === 'Farmer') {
    console.log('❌ Farmer role detected - clearing localStorage and redirecting to /login');
    localStorage.clear();
    return <Navigate to="/login" replace />;
  }
  
  // If route requires specific roles and user's role is not included
  // Only check roles if allowedRoles is provided and not empty
  if (allowedRoles.length > 0 && !allowedRoles.includes(userRole)) {
    // User's role is not in the allowed list - redirect to first accessible route
    // This handles routes like company-category-management which only allow Admin and Company
    console.log(`❌ User role '${userRole}' not in allowedRoles [${allowedRoles.join(', ')}] - redirecting to first accessible route`);
    const firstAccessibleRoute = getFirstAccessibleRoute(userRole, permissions);
    console.log(`➡️ Redirecting to: ${firstAccessibleRoute}`);
    return <Navigate to={firstAccessibleRoute} replace />;
  }
  
  // If permission is required, check it (for all users including Admin/Company)
  if (requiredPermission) {
    console.log(`🔐 Checking required permission: '${requiredPermission}'`);
    
    // Special handling for dashboard/analytics
    if (requiredPermission === 'dashboard' || requiredPermission === 'analytics') {
      console.log('📊 Analytics/Dashboard permission check');
      // Admin and Company always have access to dashboard/analytics
      if (userRole === 'Admin' || userRole === 'Company') {
        console.log(`✅ ${userRole} has access to analytics - allowing`);
        // Allow access for Admin and Company
      } else if (!permissions.dashboard && !permissions.analytics) {
        // Other roles need explicit permission for analytics
        console.log(`❌ ${userRole} doesn't have analytics/dashboard permission`);
        const firstAccessibleRoute = getFirstAccessibleRoute(userRole, permissions);
        console.log(`➡️ Redirecting to: ${firstAccessibleRoute}`);
        return <Navigate to={firstAccessibleRoute} replace />;
      } else {
        console.log(`✅ ${userRole} has analytics/dashboard permission - allowing`);
      }
    } else if (userRole === 'Admin') {
      // Admin has access to everything
      console.log('✅ Admin has access to everything - allowing');
      // No check needed, allow access
    } else if (userRole === 'Company') {
      console.log('🏢 Company role - checking company allowed permissions');
      // Company role has access to common routes even without explicit permissions
      // Map route permissions to allowed permissions for Company
      const companyAllowedPermissions = [
        'agriStore',        // products-management
        'appOrders',        // orders-management
        'bulkOrders',       // bulk-upload
        'quotation',        // Quotation-proforma
        'enquiry',          // enquiry-form
        'farmerManagement', // farmers
        'talkToAgronomist', // agronomist
        'fieldVisit',       // field-visit
        'diseaseAdvisory',  // disease-advisory
        'eventType',        // event-types
        'cropType',         // crop-types
        'farmerForum',      // posts
        'successStories',   // courses
        'cropHub'           // crop-hub
      ];
      // Company users can access these routes even without explicit permission
      // But if permission is required and not in allowed list, check if user has it
      if (!companyAllowedPermissions.includes(requiredPermission) && !permissions[requiredPermission]) {
        // Permission not in Company's allowed list and user doesn't have it
        console.log(`❌ Company doesn't have permission '${requiredPermission}' and it's not in allowed list`);
        const firstAccessibleRoute = getFirstAccessibleRoute(userRole, permissions);
        console.log(`➡️ Redirecting to: ${firstAccessibleRoute}`);
        return <Navigate to={firstAccessibleRoute} replace />;
      }
      // If permission is in allowed list, allow access regardless of permissions object
      console.log(`✅ Company has access to '${requiredPermission}' - allowing`);
    } else {
      // Other roles (co_admin, developer, accounts, operations, etc.) need explicit permission
      console.log(`👤 Other role '${userRole}' - checking explicit permission`);
      // Check if user has the required permission
      const hasPermission = permissions[requiredPermission] === true;
      console.log(`Permission '${requiredPermission}': ${hasPermission}`, { 
        permissionValue: permissions[requiredPermission],
        allPermissions: permissions 
      });
      
      if (!hasPermission) {
        // User doesn't have the required permission
        // Redirect to their first accessible route based on their actual permissions
        console.log(`❌ User '${userRole}' doesn't have permission '${requiredPermission}'`);
        const firstAccessibleRoute = getFirstAccessibleRoute(userRole, permissions);
        console.log(`➡️ Redirecting to: ${firstAccessibleRoute}`);
        return <Navigate to={firstAccessibleRoute} replace />;
      }
      // User has the required permission, allow access
      console.log(`✅ User '${userRole}' has permission '${requiredPermission}' - allowing`);
    }
  } else {
    console.log('ℹ️ No required permission - allowing access');
  }

  // Redirect from dashboard root to appropriate page based on permissions
  if (location.pathname === '/dashboard') {
    console.log('🏠 Dashboard root path - redirecting to first accessible route');
    // Use the helper function to get the first accessible route
    // This ensures consistency and matches navigation order
    const firstAccessibleRoute = getFirstAccessibleRoute(userRole, permissions);
    console.log(`➡️ Redirecting from /dashboard to: ${firstAccessibleRoute}`);
    return <Navigate to={firstAccessibleRoute} replace />;
  }

  console.log('✅ All checks passed - allowing access to:', location.pathname);
  return children;
};

export default ProtectedRoute; 